import cx from "classnames";
import React, { useState } from "react";

import { counsellingsPostDetailUrl, counsellingsTopicUrl } from "@/mk2/apps/counsellings/urls";
import { forumPostDetailUrl } from "@/mk2/apps/forum/urls";
import { LinkedObjects as LinkedObjectsType } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions";
import { Dot } from "@/mk2/components/Dot";
import { Link } from "@/mk2/components/Link";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import {
    STROLLERS_COUNSELLING,
    STROLLER_DETAIL_LINKED_OBJECTS_COUNSELLING,
    STROLLER_DETAIL_LINKED_OBJECTS_DISCUSSION,
    STROLLER_DETAIL_LINKED_OBJECTS_EXPAND,
} from "@/mk/autogenerated/translation";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./LinkedObjects.mscss";

interface OwnProps {
    className?: string;
    header: string;
    linkedObjects: LinkedObjectsType;
    addButton?: React.ReactNode;
}

type Props = OwnProps;

const LIMIT = 3;

export const LinkedObjects: React.FunctionComponent<Props> = React.memo(
    ({ className, header, linkedObjects, addButton }) => {
        const [expanded, setExpanded] = useState<boolean>(false);

        const sortedObjects = linkedObjects.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        const objectsToRender = expanded ? sortedObjects : sortedObjects.slice(0, LIMIT);

        const showMore = linkedObjects.length > 0 && objectsToRender.length < linkedObjects.length;

        const onExpandClick = useEventCallback(() => {
            setExpanded(true);
        }, [setExpanded]);

        return (
            <div className={cx(styles.LinkedObjects, className)}>
                <h2 className={styles.LinkedObjects__headline}>{header}</h2>
                {objectsToRender.map((linkedObject) => (
                    <Link
                        key={linkedObject.id}
                        className={styles.LinkedObjects__linkedObject}
                        to={url(
                            linkedObject.isCounselling
                                ? counsellingsPostDetailUrl
                                : forumPostDetailUrl,
                            {
                                topicSlug: linkedObject.topicSlug,
                                postSlug: linkedObject.slug,
                            },
                        )}
                    >
                        <span className={styles.LinkedObjects__linkedObject__title}>
                            {linkedObject.title}
                        </span>
                        <div className={styles.LinkedObjects__linkedObject__footer}>
                            {(linkedObject.isCounselling &&
                                ACTIVE_SERVER_ID === 201 &&
                                linkedObject.topicSlug === "kocikova-poradna") ||
                            (ACTIVE_SERVER_ID === 202 &&
                                linkedObject.topicSlug === "kocarkova-poradna") ? (
                                <Link
                                    to={url(counsellingsTopicUrl, {
                                        topicSlug: linkedObject.topicSlug,
                                    })}
                                >
                                    {STROLLERS_COUNSELLING}
                                </Link>
                            ) : (
                                <span className={styles.LinkedObjects__linkedObject__footer__type}>
                                    {linkedObject.isCounselling
                                        ? STROLLER_DETAIL_LINKED_OBJECTS_COUNSELLING
                                        : STROLLER_DETAIL_LINKED_OBJECTS_DISCUSSION}
                                </span>
                            )}

                            <Dot />
                            <span className={styles.LinkedObjects__linkedObject__footer__date}>
                                {timeAt(linkedObject.createdAt)}
                            </span>
                        </div>
                    </Link>
                ))}

                <div
                    className={cx(
                        styles.LinkedObjects__footer,
                        !showMore && styles["LinkedObjects__footer--noMore"],
                    )}
                >
                    {showMore && (
                        <button className={styles.LinkedObjects__expand} onClick={onExpandClick}>
                            {STROLLER_DETAIL_LINKED_OBJECTS_EXPAND} ({linkedObjects.length})
                        </button>
                    )}

                    {addButton}
                </div>
            </div>
        );
    },
);

LinkedObjects.displayName = "LinkedObjects";
